import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import {
  validateDate,
  validateNumber,
  validatePresence
} from 'ember-changeset-validations/validators';
import { BufferedChangeset } from 'ember-changeset/types';
import moment from 'moment-timezone';
import CustomFieldDefinition from 'volta/models/custom-field-definition';
import {
  TCustomFieldTableValue,
  TCustomFieldValues
} from 'volta/models/types/customFieldDefinition';
import { parseNumber } from 'volta/utils/math-utils';

import { action } from '@ember/object';
import { next } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

interface IArgs {
  definitions: CustomFieldDefinition[];
  values: TCustomFieldValues;
  onInit?: (isInvalid: boolean) => void;
  onChange?: (values: TCustomFieldValues, isInvalid: boolean) => void;
}

export default class CustomFieldsEditor extends Component<IArgs> {
  // Services
  // ~~~~~

  @tracked declare changeset: BufferedChangeset;
  @tracked values?: TCustomFieldValues = this.args.values;

  // Properties
  // ~~~~~

  validations: object = {};
  definitionsById: Record<string, CustomFieldDefinition> = {};

  // Computed Properties
  // ~~~~~

  get valuesById() {
    const values: TCustomFieldValues = {};
    const { definitions } = this.args;
    definitions.forEach((d) => {
      values[d.id] = this.changeset.get(d.name);
    });
    return values;
  }

  // Lifecycle Hooks
  // ~~~~~

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    this.initChangeset();
  }

  // Actions
  // ~~~~~

  @action
  handleValuesChange() {
    next(() => {
      this.initChangeset();
    });
  }

  @action
  handleSingleValueChange(name: string, value: TCustomFieldTableValue) {
    this.changeset.set(name, value);
    this.submit();
  }

  @action
  validate() {
    this.changeset.validate();
  }

  // Helper Functions
  // ~~~~~

  /**
   * Build a values structure where the key is the name not the slug
   * We do that to ease the use of ember-changeset-validations, so that
   * error message use the correct field name and not the slug
   *
   * @param values Def slug (or id) / value pairs
   * @param definitions List of custom field definitions
   */
  buildValues(values: TCustomFieldValues = {}, definitions: CustomFieldDefinition[] = []) {
    const returnValues: TCustomFieldValues = {};
    this.definitionsById = {};
    definitions.forEach((d) => {
      this.definitionsById[d.id] = d;
      let value = values[d.slug] || values[d.id];

      // Avoid NaN if empty
      if (d.fieldType === 'NUMBER' && typeof value === 'number') {
        value = parseNumber(value);
      }

      if (value) {
        // We need to do parse the value as a Javascript Date because
        // ember-changeset-validation won't recognize a date formatted as a String
        if (d.fieldType === 'DATE') {
          value = moment(value as string).toDate();
        }

        if (d.fieldType === 'BOOLEAN') {
          value = `${value}`.toLowerCase() === 'true';
        }
      }

      returnValues[d.name] = value;
    });
    return returnValues;
  }

  /**
   * Builds the fields validations
   *
   * @param definitions List of definitions
   */
  buildValidations(definitions: CustomFieldDefinition[] = []) {
    const validations: Record<string, any> = {};
    definitions.forEach((def) => {
      const defValidations = [];
      const { fieldConstraints, fieldType, name, required } = def;

      if (required && fieldType !== 'BOOLEAN') {
        defValidations.push(validatePresence(true));
      }

      if (fieldConstraints) {
        const { gte, lte, onOrBefore, onOrAfter } = fieldConstraints;

        if (fieldType === 'NUMBER' && (lte || gte)) {
          defValidations.push(
            validateNumber({
              lte: lte && parseNumber(lte),
              gte: gte && parseNumber(gte),
              allowBlank: !required
            })
          );
        }

        if (fieldType === 'DATE' && (onOrBefore || onOrAfter)) {
          defValidations.push(
            validateDate({
              onOrAfter: onOrAfter && moment(onOrAfter).toDate(),
              onOrBefore: onOrBefore && moment(onOrBefore).toDate(),
              allowBlank: !required
            })
          );
        }
      }

      validations[name] = defValidations;
    });

    return validations;
  }

  /**
   * Initialises the Changeset state
   */
  initChangeset() {
    this.validations = this.buildValidations(this.args.definitions);
    this.values = this.buildValues(this.args.values, this.args.definitions);
    const changeset = Changeset(this.values, lookupValidator(this.validations), this.validations);
    this.changeset = changeset;
    next(() => {
      this.changeset.validate();
    });
  }

  submit() {
    this.changeset.validate();
    this.args.onChange?.(this.valuesById, this.changeset.isInvalid);
  }
}
